/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react'
import { FilterState, RelayData } from 'src/constants/DataModels'

import { formatAge, formatAgeShort, formatBridgeId, formatChain, formatDurationShort, formatPayloadType, formatRelayStatus, formatTime, formatUtc } from 'src/constants/ConversionUtils'
import { StatsState } from 'src/store/stats/StatsTypes'
import { useAppSelector } from 'src/store/store'
import 'src/components/Relays/RelaysPage.css'
import { BridgeId, FlowType } from 'src/constants/BlockchainConstants'
import FlowTypeTag from 'src/components/shared/FlowTypeTag'
import { BlockchainWithIcon } from 'src/components/shared/BlockchainWithIcon'
import { TransferInfo } from 'src/components/shared/TransferInfo'
import StatusTag from 'src/components/shared/StatusTag'
import { DetailsColumn } from 'src/components/Tables/DetailsColumn'
import ServerTable, { Column, FilterConfig, RequestDataParams } from 'src/components/Tables/ServerTable'
import { fetchRelaysData, formatParamsForApi } from 'src/api/api'
import { useSearchParams } from 'react-router-dom'
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from 'src/components/Tables/DateRangeFilter'

export const RELAY_COLUMNS: Column<RelayData>[] = [
  {
    id: 'created_at',
    header: 'Date/Time',
    canSort: true,
    cellRenderer: (row) => {
      const localTime = formatTime(row.created_at)
      const age = formatAge(row.created_at)
      const utc = formatUtc(row.created_at)
      return (
        <div className="date-column" title={`UTC: ${utc}`}>
          {localTime}
          <br />
          {age}
        </div>
      )
    },
  },
  {
    id: 'bridge_id',
    header: 'Bridge',
    canSort: true,
    cellRenderer: (row) => {
      return formatBridgeId(row.bridge_id)
    },
  },
  {
    id: 'flow_type',
    header: 'In/Out',
    canSort: true,
    cellRenderer: (row) => {
      return (
        <FlowTypeTag flowType={row.flow_type} />
      )
    },
  },
  {
    id: 'destination_blockchain',
    header: 'Blockchain',
    canSort: true,
    cellRenderer: (row) => {
      const { flow_type, source_blockchain, destination_blockchain } = row
      const blockchain = flow_type === FlowType.In ? source_blockchain : destination_blockchain
      return BlockchainWithIcon({ blockchain })
    },
  },
  {
    id: 'payload_type',
    header: 'Type',
    canSort: true,
    cellRenderer: (row) => {
      return (
        <div className="capitalize" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {formatPayloadType(row.bridge_id, row.flow_type as FlowType, row.payload_type)}
        </div>
      )
    },
  },
  {
    id: 'payload',
    header: 'Payload',
    cellRenderer: (row) => {
      const { source_blockchain, destination_blockchain, payload } = row
      if (!payload || !payload.transfer_amount_human) return <div>Info In Details</div>
      return (
        <TransferInfo
          source_blockchain={source_blockchain}
          destination_blockchain={destination_blockchain}
          payload={payload}
        />
      )
    },
  },
  {
    id: 'status',
    header: 'Status',
    canSort: true,
    cellRenderer: (row) => {
      const { start_block_time, end_block_time, status } = row
      const shortDuration = formatDurationShort(start_block_time, end_block_time)
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
        >
          {StatusTag({ status })}
          {shortDuration && (
            <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
              ⏲️ {shortDuration}
            </span>
          )}
        </div>
      )
    },
  },
  {
    id: 'details',
    header: '',
    cellRenderer: (row) => {
      return <DetailsColumn path={`/relays/${row.id}`} />
    },
  },
]

export const MOBILE_RELAY_COLUMNS: Column<RelayData>[] = [
  {
    id: 'created_at',
    header: 'Age',
    canSort: true,
    cellRenderer: (row) => {
      const age = formatAgeShort(row.created_at)
      const utc = formatUtc(row.created_at)
      return (
        <div className="age-col" title={`UTC: ${utc}`}>
          {age}
        </div>
      )
    },
  },
  {
    id: 'payload',
    header: 'Payload',
    cellRenderer: (row) => {
      const { bridge_id, flow_type, payload_type, source_blockchain, destination_blockchain, status, payload } = row
      const canDisplayPayload = payload && !!payload.transfer_amount_human
      const blockchain = flow_type === FlowType.In ? source_blockchain : destination_blockchain
      return (
        <div className={'mobile-payload-wrapper'}>
          <div className={'relay-info-line'}>
            <FlowTypeTag flowType={row.flow_type} />
            <BlockchainWithIcon blockchain={blockchain} />
            <StatusTag status={status} iconOnly={true} />
          </div>
          <div className={'relay-info-payload-type'}>Type: {formatPayloadType(bridge_id, flow_type as FlowType, payload_type)}</div>
          {
            canDisplayPayload ? (
              <TransferInfo
                source_blockchain={source_blockchain}
                destination_blockchain={destination_blockchain}
                payload={payload}
              />
            ) : 'Info in details'}
        </div>
      )
    },
  },
  {
    id: 'details',
    header: '',
    cellRenderer: (row) => {
      return <DetailsColumn path={`/relays/${row.id}`} iconOnly={true} />
    },
  },
]

const TYPE_OPTIONS_BY_FLOW: Record<string, Array<Record<string, string>>> = {
  in: [
    { value: '0', label: 'Deposit' },
    { value: '1', label: 'Deposit And Execute' },
    { value: '2', label: 'Execute Carbon' },
    { value: '3', label: 'Confirmation Callback' },
    { value: 'transfer', label: '[PN/IBC] Transfer' },
    { value: 'token_transfer', label: '[PN/IBC] Token Transfer' },
    { value: 'token_bridging', label: '[PN] Token Bridging' },
  ],
  out: [
    { value: '0', label: 'Withdraw' },
    { value: '1', label: 'Execute Gateway' },
    { value: '2', label: 'Withdraw And Execute' },
    { value: '3', label: 'Register Token' },
    { value: '4', label: 'Deregister Token' },
    { value: 'transfer', label: '[PN/IBC] Transfer' },
    { value: 'token_transfer', label: '[PN/IBC] Token Transfer' },
    { value: 'token_bridging', label: '[PN] Token Bridging' },
    { value: 'register_asset', label: '[PN] Register Asset' },
  ],
}

export const DEFAULT_START_DATE_FORMAT = formatUtc(
  DEFAULT_START_DATE
)
export const DEFAULT_END_DATE_FORMAT = formatUtc(
  DEFAULT_END_DATE
)

const RelaysPage: React.FC = () => {
  const selector = useAppSelector

  // state from supported_blockchains
  const statsState: StatsState = selector((state) => state.StatsReducer)
  const supportedBlockchains = statsState.supportedBlockchains.data
  const [searchParams] = useSearchParams()

  // request function
  const handleDataRequest = useCallback(async (params: RequestDataParams) => {
    // add default date range if none exists
    const filterWithDefaults: FilterState = {
      ...params.filter,
      start_time: params.filter.start_time || DEFAULT_START_DATE_FORMAT,
      end_time: params.filter.end_time || DEFAULT_END_DATE_FORMAT,
    }

    const response = await fetchRelaysData(formatParamsForApi({
      ...params,
      filter: filterWithDefaults,
    }))
    return response.data
  }, [])

  // derive filter options
  const bridgeOptions = useMemo(() => {
    return Array.from(new Set(supportedBlockchains.map((row) => row.bridge_id)))
  }, [supportedBlockchains])
  const filterState: FilterState = Object.fromEntries(searchParams.entries())
  const flowType = filterState.flow_type
  const selectedBridgeId = filterState.bridge_id
  const typeOptions = flowType ? TYPE_OPTIONS_BY_FLOW[flowType] : []
  const blockchainOptions = useMemo(() => {
    return Array.from(new Set(
      supportedBlockchains
        .filter((row) => row.blockchain !== 'polynetwork')
        .filter((row) => !selectedBridgeId || row.bridge_id.toString() === selectedBridgeId)
        .map((row) => row.blockchain)
    )).sort()
  }, [supportedBlockchains, selectedBridgeId])

  // filter options
  const filters: FilterConfig[] = useMemo(() => ([
    {
      name: 'Date',
      id: 'start_time,end_time',
      filterType: 'daterange',
    },
    {
      name: 'Bridge',
      id: 'bridge_id',
      filterType: 'select',
      options: bridgeOptions.map((opt) => opt.toString()),
      optionFormatter: (val: string) => formatBridgeId(Number(val) as BridgeId),
    },
    {
      name: 'In/Out',
      id: 'flow_type',
      filterType: 'select',
      options: [FlowType.In, FlowType.Out],
      optionFormatter: (val: string) => val.toUpperCase(),
    },
    {
      name: 'Blockchain',
      id: 'blockchain',
      filterType: 'select',
      options: blockchainOptions,
      optionFormatter: formatChain,
    },
    {
      name: 'Status',
      id: 'status',
      filterType: 'select',
      options: ['in_transit', 'completed', 'refunded'],
      optionFormatter: formatRelayStatus,
    },
    {
      name: 'Search Tx/Addr',
      id: 'search_term',
      filterType: 'text',
    },
  ]), [bridgeOptions, blockchainOptions])

  const filtersWithType = useMemo(() => {
    if (!flowType) return filters

    const typeFilter: FilterConfig = {
      name: 'Type',
      id: 'payload_type',
      filterType: 'select',
      options: typeOptions.map((opt) => opt.value),
      optionFormatter: (val: string) => {
        const match = typeOptions.find(opt => opt.value === val)
        return match ? match.label : val
      },
    }

    // Insert after 'In/Out' filter (id === 'flow_type')
    const insertIndex = filters.findIndex((f) => f.id === 'flow_type') + 1
    return [...filters.slice(0, insertIndex), typeFilter, ...filters.slice(insertIndex)]
  }, [filters, flowType, typeOptions])

  return (
    <div className="container main-wrapper">
      <ServerTable name="Relays"
                   columns={RELAY_COLUMNS}
                   mobileColumns={MOBILE_RELAY_COLUMNS}
                   onRequestData={handleDataRequest}
                   filters={filtersWithType}
      />
    </div>
  )
}

export default RelaysPage
